// Messagerie interne : discussions privées et groupes, pièces jointes, « vu », et (administrateur) utilisateurs connectés.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Badge, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, List, ListItemAvatar, ListItemButton, ListItemText, Menu, MenuItem,
  Paper, Tab, Tabs, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  AddCommentOutlined, ArrowBack, AttachFileOutlined, CloseOutlined, DeleteOutline, DescriptionOutlined, DoneAll,
  DownloadOutlined, GroupAddOutlined, GroupsOutlined, InfoOutlined, InsertDriveFileOutlined, PersonAddAltOutlined,
  PictureAsPdfOutlined, SearchOutlined, SendRounded, TableChartOutlined, ChatBubbleOutline, CircleRounded,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { chatAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { refreshChatUnread } from '../hooks/useChatUnread';
import { apiError } from '../utils/format';
import { tokens } from '../theme';

const MAX_FICHIER = 10 * 1024 * 1024;
const PALETTE = ['#1E3A66', '#1B7A46', '#B7791F', '#7B3F98', '#2C6BA0', '#A8432A', '#3D6B6B', '#5B6675'];
const couleur = (id) => PALETTE[Math.abs(Number(id) || 0) % PALETTE.length];
const initiales = (n = '') => n.split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
const taille = (o) => (o >= 1048576 ? `${(o / 1048576).toFixed(1).replace('.', ',')} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`);
const heure = (d) => new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const jourCle = (d) => new Date(d).toDateString();
function jourLibelle(d) {
  const x = new Date(d); const auj = new Date(); const hier = new Date(); hier.setDate(auj.getDate() - 1);
  if (x.toDateString() === auj.toDateString()) return "Aujourd'hui";
  if (x.toDateString() === hier.toDateString()) return 'Hier';
  return x.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: x.getFullYear() === auj.getFullYear() ? undefined : 'numeric' });
}
function quandCourt(d) {
  if (!d) return '';
  const x = new Date(d); const auj = new Date();
  if (x.toDateString() === auj.toDateString()) return heure(d);
  const hier = new Date(); hier.setDate(auj.getDate() - 1);
  if (x.toDateString() === hier.toDateString()) return 'Hier';
  return x.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}
function depuis(d) {
  const min = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (min < 2) return 'actif maintenant';
  if (min < 60) return `il y a ${min} min`;
  if (min < 1440) return `il y a ${Math.round(min / 60)} h`;
  return `le ${new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} à ${heure(d)}`;
}

/** Texte avec liens cliquables. */
function Texte({ children }) {
  const parts = String(children || '').split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) => (/^https?:\/\//.test(p)
    ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}>{p}</a>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

function UserAvatar({ id, nom, size = 40, groupe = false, enLigne = false }) {
  const a = (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.38, fontWeight: 600, bgcolor: groupe ? tokens.amber : couleur(id), color: groupe ? tokens.ink : '#fff' }}>
      {groupe ? <GroupsOutlined sx={{ fontSize: size * 0.55 }} /> : initiales(nom)}
    </Avatar>
  );
  if (!enLigne) return a;
  return (
    <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={<Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#2EAD5B', border: '2px solid #fff' }} />}>{a}</Badge>
  );
}

// ---------------------------------------------------------------------
// Pièces jointes (téléchargées avec le jeton, affichées en blob)
// ---------------------------------------------------------------------
const blobCache = new Map();
async function blobUrl(fichierId) {
  if (!blobCache.has(fichierId)) {
    blobCache.set(fichierId, chatAPI.telecharger(fichierId).then((r) => URL.createObjectURL(r.data)).catch((e) => { blobCache.delete(fichierId); throw e; }));
  }
  return blobCache.get(fichierId);
}
async function telecharger(f) {
  const url = await blobUrl(f.id);
  const a = document.createElement('a'); a.href = url; a.download = f.nom; document.body.appendChild(a); a.click(); a.remove();
}
const estImage = (t) => /^image\/(png|jpe?g|gif|webp)$/.test(t || '');
const IconeFichier = ({ type }) => {
  if (/pdf/.test(type)) return <PictureAsPdfOutlined sx={{ color: '#B4232A' }} />;
  if (/sheet|excel|csv/.test(type)) return <TableChartOutlined sx={{ color: '#1B7A46' }} />;
  if (/word|document|text/.test(type)) return <DescriptionOutlined sx={{ color: '#2C6BA0' }} />;
  return <InsertDriveFileOutlined sx={{ color: tokens.steel }} />;
};

function Image({ fichier, onOpen }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { let ok = true; blobUrl(fichier.id).then((u) => ok && setSrc(u)).catch(() => ok && setErr(true)); return () => { ok = false; }; }, [fichier.id]);
  if (err) return <Typography sx={{ fontSize: 13, opacity: 0.8 }}>Image indisponible</Typography>;
  return (
    <Box component="button" onClick={() => src && onOpen(src)} aria-label={`Agrandir ${fichier.nom}`}
      sx={{ all: 'unset', cursor: 'zoom-in', display: 'block', borderRadius: 1.5, overflow: 'hidden', bgcolor: 'rgba(0,0,0,.06)', minWidth: 120, minHeight: 90 }}>
      {src ? <Box component="img" src={src} alt={fichier.nom} sx={{ display: 'block', maxWidth: { xs: 220, sm: 280 }, maxHeight: 280, objectFit: 'cover' }} />
        : <Box sx={{ width: 160, height: 110, display: 'grid', placeItems: 'center' }}><CircularProgress size={20} /></Box>}
    </Box>
  );
}

function Fichier({ fichier, mine }) {
  const [busy, setBusy] = useState(false);
  return (
    <Box component="button" disabled={busy} onClick={async () => { setBusy(true); try { await telecharger(fichier); } finally { setBusy(false); } }}
      sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.25, p: 1, pr: 1.5, borderRadius: 1.5, minWidth: 200, maxWidth: 300,
        bgcolor: mine ? 'rgba(255,255,255,.12)' : '#fff', border: mine ? '1px solid rgba(255,255,255,.2)' : `1px solid ${tokens.lineSoft}` }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}><IconeFichier type={fichier.type} /></Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }}>{fichier.nom}</Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.75 }}>{taille(fichier.taille)}</Typography>
      </Box>
      {busy ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <DownloadOutlined fontSize="small" sx={{ opacity: 0.8 }} />}
    </Box>
  );
}

// ---------------------------------------------------------------------
// Choix de collègues (nouvelle discussion, groupe, ajout de membres)
// ---------------------------------------------------------------------
function ChoixCollegues({ users, value, onChange, label = 'Membres', exclus = [] }) {
  const options = users.filter((u) => !exclus.includes(u.id));
  return (
    <Autocomplete multiple disableCloseOnSelect options={options} value={options.filter((u) => value.includes(u.id))}
      onChange={(e, v) => onChange(v.map((u) => u.id))} getOptionLabel={(u) => u.nom} isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText="Aucun collègue"
      renderOption={(props, u, { selected }) => (
        <li {...props} key={u.id}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
          <UserAvatar id={u.id} nom={u.nom} size={28} />
          <Box sx={{ ml: 1.25 }}><Typography sx={{ fontSize: 14 }}>{u.nom}</Typography>{u.fonction && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{u.fonction}</Typography>}</Box>
        </li>
      )}
      renderInput={(p) => <TextField {...p} label={label} placeholder="Rechercher un collègue" />} />
  );
}

function NouvelleDiscussion({ open, onClose, users, onPrive, onGroupe }) {
  const [mode, setMode] = useState('prive');
  const [q, setQ] = useState('');
  const [nom, setNom] = useState('');
  const [membres, setMembres] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setQ(''); setNom(''); setMembres([]); setError(''); } }, [open]);
  const filtres = users.filter((u) => `${u.nom} ${u.fonction || ''}`.toLowerCase().includes(q.toLowerCase()));
  const creer = async () => {
    setBusy(true); setError('');
    try { await onGroupe({ nom, membres }); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0 }}>Nouvelle discussion</DialogTitle>
      <Tabs value={mode} onChange={(e, v) => setMode(v)} sx={{ px: 2 }}>
        <Tab value="prive" label="Privée" icon={<PersonAddAltOutlined fontSize="small" />} iconPosition="start" sx={{ minHeight: 48 }} />
        <Tab value="groupe" label="Groupe" icon={<GroupAddOutlined fontSize="small" />} iconPosition="start" sx={{ minHeight: 48 }} />
      </Tabs>
      <Divider />
      {mode === 'prive' ? (
        <DialogContent sx={{ p: 0, height: 420, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <TextField fullWidth size="small" autoFocus placeholder="Rechercher un collègue" value={q} onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
          </Box>
          <List sx={{ overflowY: 'auto', flex: 1, pt: 0 }}>
            {filtres.map((u) => (
              <ListItemButton key={u.id} onClick={() => onPrive(u.id)}>
                <ListItemAvatar><UserAvatar id={u.id} nom={u.nom} size={36} /></ListItemAvatar>
                <ListItemText primary={u.nom} secondary={u.fonction} />
              </ListItemButton>
            ))}
            {!filtres.length && <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: 14 }}>Aucun collègue trouvé</Typography>}
          </List>
        </DialogContent>
      ) : (
        <>
          <DialogContent sx={{ display: 'grid', gap: 2, pt: '20px !important' }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Nom du groupe" required autoFocus value={nom} onChange={(e) => setNom(e.target.value)} inputProps={{ maxLength: 80 }}
              placeholder="ex. Équipe production, Chantier Mbour" />
            <ChoixCollegues users={users} value={membres} onChange={setMembres} />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Vous serez administrateur du groupe : vous pourrez le renommer et gérer ses membres.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Annuler</Button>
            <Button variant="contained" disabled={busy || !nom.trim() || !membres.length} onClick={creer}>Créer le groupe</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

function InfosGroupe({ open, onClose, conv, users, moiId, onChange, onQuitte }) {
  const [nom, setNom] = useState('');
  const [ajout, setAjout] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && conv) { setNom(conv.nom || ''); setAjout([]); setError(''); } }, [open, conv]);
  if (!conv) return null;
  const run = async (fn) => { setBusy(true); setError(''); try { await fn(); await onChange(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); } };
  const gerer = conv.peut_gerer;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{conv.type === 'GROUPE' ? 'Infos du groupe' : 'Discussion privée'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {conv.type === 'GROUPE' && gerer && (
          <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
            <TextField fullWidth size="small" label="Nom du groupe" value={nom} onChange={(e) => setNom(e.target.value)} inputProps={{ maxLength: 80 }} />
            <Button variant="outlined" disabled={busy || !nom.trim() || nom.trim() === conv.nom} onClick={() => run(() => chatAPI.renommer(conv.id, nom))}>Renommer</Button>
          </Box>
        )}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{conv.membres.length} membre(s)</Typography>
          <List dense disablePadding>
            {conv.membres.map((m) => (
              <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, borderBottom: `1px solid ${tokens.lineSoft}` }}>
                <UserAvatar id={m.id} nom={m.nom} size={32} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 500 }}>{m.nom}{m.id === moiId ? ' (vous)' : ''}{!m.actif ? ' — désactivé' : ''}</Typography>
                  {m.fonction && <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>{m.fonction}</Typography>}
                </Box>
                {m.admin && conv.type === 'GROUPE' && <Chip size="small" label="Admin du groupe" variant="outlined" />}
                {gerer && m.id !== moiId && (
                  <>
                    <Button size="small" disabled={busy} onClick={() => run(() => chatAPI.setAdmin(conv.id, m.id, !m.admin))}>{m.admin ? 'Retirer admin' : 'Nommer admin'}</Button>
                    <Tooltip title="Retirer du groupe"><IconButton size="small" disabled={busy} aria-label={`Retirer ${m.nom}`}
                      onClick={() => window.confirm(`Retirer ${m.nom} du groupe ?`) && run(() => chatAPI.retirer(conv.id, m.id))}><CloseOutlined fontSize="small" /></IconButton></Tooltip>
                  </>
                )}
              </Box>
            ))}
          </List>
        </Box>
        {conv.type === 'GROUPE' && gerer && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}><ChoixCollegues users={users} value={ajout} onChange={setAjout} label="Ajouter des membres" exclus={conv.membres.map((m) => m.id)} /></Box>
            <Button variant="contained" sx={{ mt: 1 }} disabled={busy || !ajout.length} onClick={() => run(async () => { await chatAPI.ajouter(conv.id, ajout); setAjout([]); })}>Ajouter</Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: conv.type === 'GROUPE' ? 'space-between' : 'flex-end' }}>
        {conv.type === 'GROUPE' && (
          <Button color="error" disabled={busy} onClick={() => window.confirm('Quitter ce groupe ? Vous ne verrez plus ses messages.') && run(async () => { await chatAPI.retirer(conv.id, moiId); onQuitte(); })}>
            Quitter le groupe
          </Button>
        )}
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Fil d'une discussion
// ---------------------------------------------------------------------
function Fil({ id, moiId, isAdmin, users, onBack, onActivity, onQuitte }) {
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [luParTous, setLuParTous] = useState(0);
  const [plusAnciens, setPlusAnciens] = useState(false);
  const [error, setError] = useState('');
  const [texte, setTexte] = useState('');
  const [fichier, setFichier] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [infos, setInfos] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [menu, setMenu] = useState(null); // { anchor, message }
  const [drag, setDrag] = useState(false);
  const scroller = useRef(null);
  const collerEnBas = useRef(true);
  const dernierLu = useRef(0);
  const fileInput = useRef(null);
  const input = useRef(null);

  const chargerConv = useCallback(() => chatAPI.conversation(id).then((r) => setConv(r.data)), [id]);
  useEffect(() => {
    setConv(null); setMessages([]); setError(''); setTexte(''); setFichier(null); collerEnBas.current = true; dernierLu.current = 0;
    chargerConv().catch((e) => setError(apiError(e)));
    chatAPI.messages(id).then((r) => { setMessages(r.data.messages); setLuParTous(r.data.lu_par_tous); setPlusAnciens(r.data.plus_anciens); }).catch(() => {});
  }, [id, chargerConv]);

  const dernierId = messages.length ? messages[messages.length - 1].id : 0;
  // Nouveaux messages toutes les 4 s
  useEffect(() => {
    const t = setInterval(() => {
      chatAPI.messages(id, { apres: dernierId }).then((r) => {
        setLuParTous(r.data.lu_par_tous);
        if (r.data.messages.length) {
          const el = scroller.current;
          collerEnBas.current = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120;
          setMessages((prev) => [...prev, ...r.data.messages.filter((m) => !prev.some((p) => p.id === m.id))]);
          if (r.data.messages.some((m) => m.systeme)) chargerConv().catch(() => {});
          onActivity();
        }
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [id, dernierId, onActivity, chargerConv]);

  // Lecture : la discussion affichée est lue
  useEffect(() => {
    if (!dernierId || dernierId <= dernierLu.current || document.visibilityState !== 'visible') return;
    dernierLu.current = dernierId;
    chatAPI.lu(id, dernierId).then(() => { refreshChatUnread(); onActivity(); }).catch(() => {});
  }, [id, dernierId, onActivity]);

  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && collerEnBas.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const chargerAnciens = async () => {
    const el = scroller.current; const avant = el?.scrollHeight || 0;
    const r = await chatAPI.messages(id, { avant: messages[0]?.id });
    collerEnBas.current = false;
    setMessages((prev) => [...r.data.messages, ...prev]);
    setPlusAnciens(r.data.plus_anciens);
    requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - avant; });
  };

  const choisirFichier = (f) => {
    if (!f) return;
    if (f.size > MAX_FICHIER) { setError(`« ${f.name} » dépasse 10 Mo (${taille(f.size)})`); return; }
    setError(''); setFichier(f); input.current?.focus();
  };

  const envoyer = async () => {
    const t = texte.trim();
    if ((!t && !fichier) || envoi) return;
    setEnvoi(true); setError('');
    try {
      const r = fichier ? await chatAPI.fichier(id, fichier, t) : await chatAPI.envoyer(id, t);
      collerEnBas.current = true;
      setMessages((prev) => (prev.some((m) => m.id === r.data.id) ? prev : [...prev, r.data]));
      dernierLu.current = r.data.id;
      setTexte(''); setFichier(null);
      onActivity();
    } catch (e) { setError(apiError(e)); } finally { setEnvoi(false); input.current?.focus(); }
  };

  const supprimer = async (m) => {
    setMenu(null);
    if (!window.confirm('Supprimer ce message pour tout le monde ?')) return;
    try {
      await chatAPI.supprimer(m.id);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, supprime: true, contenu: null, fichier: null } : x)));
      onActivity();
    } catch (e) { setError(apiError(e)); }
  };

  const groupe = conv?.type === 'GROUPE';
  const sousTitre = !conv ? '' : groupe
    ? conv.membres.map((m) => (m.id === moiId ? 'vous' : m.nom.split(' ')[0])).join(', ')
    : [conv.interlocuteur?.fonction, conv.interlocuteur && !conv.interlocuteur.actif ? 'compte désactivé' : null].filter(Boolean).join(' · ');
  const monDernier = [...messages].reverse().find((m) => m.user_id === moiId && !m.systeme && !m.supprime);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, position: 'relative' }}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); setDrag(false); choisirFichier(e.dataTransfer.files?.[0]); }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: { xs: 1, sm: 2 }, py: 1.25, borderBottom: `1px solid ${tokens.line}`, bgcolor: '#fff' }}>
        {onBack && <IconButton onClick={onBack} aria-label="Retour aux discussions"><ArrowBack /></IconButton>}
        {conv && <UserAvatar id={conv.interlocuteur?.id} nom={conv.titre} groupe={groupe} size={38} />}
        <Box sx={{ flex: 1, minWidth: 0, cursor: groupe ? 'pointer' : 'default' }} onClick={() => groupe && setInfos(true)}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 15.5 }}>{conv?.titre || ' '}</Typography>
          <Typography noWrap sx={{ fontSize: 12.5, color: 'text.secondary' }}>{sousTitre}</Typography>
        </Box>
        {conv && <Tooltip title={groupe ? 'Infos du groupe' : 'Infos'}><IconButton onClick={() => setInfos(true)} aria-label="Infos de la discussion"><InfoOutlined /></IconButton></Tooltip>}
      </Box>

      {/* Messages */}
      <Box ref={scroller} role="log" aria-label="Fil de la discussion" sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.25, sm: 3 }, py: 2, bgcolor: '#F3F5F8',
        backgroundImage: 'radial-gradient(rgba(18,40,76,.05) 1px, transparent 1px)', backgroundSize: '18px 18px' }}>
        {plusAnciens && <Box sx={{ textAlign: 'center', mb: 1 }}><Button size="small" onClick={chargerAnciens}>Messages précédents</Button></Box>}
        {conv && !messages.length && (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 6 }}>
            <ChatBubbleOutline sx={{ fontSize: 40, opacity: 0.4 }} />
            <Typography sx={{ mt: 1 }}>Aucun message. Écrivez le premier !</Typography>
          </Box>
        )}
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const nouveauJour = !prev || jourCle(prev.created_at) !== jourCle(m.created_at);
          const sep = nouveauJour && (
            <Box key={`j${m.id}`} sx={{ textAlign: 'center', my: 1.5 }}>
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: tokens.steel, bgcolor: '#fff', px: 1.5, py: 0.4, borderRadius: 5, border: `1px solid ${tokens.lineSoft}`, textTransform: 'capitalize' }}>
                {jourLibelle(m.created_at)}
              </Typography>
            </Box>
          );
          if (m.systeme) {
            return (
              <React.Fragment key={m.id}>{sep}
                <Typography sx={{ textAlign: 'center', fontSize: 12.5, color: tokens.steel, my: 1, fontStyle: 'italic' }}>{m.contenu}</Typography>
              </React.Fragment>
            );
          }
          const mine = m.user_id === moiId;
          const suite = prev && !nouveauJour && !prev.systeme && prev.user_id === m.user_id
            && new Date(m.created_at) - new Date(prev.created_at) < 5 * 60000;
          const peutSupprimer = !m.supprime && (mine || isAdmin);
          return (
            <React.Fragment key={m.id}>{sep}
              <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', gap: 1, mt: suite ? 0.4 : 1.25, alignItems: 'flex-end',
                '&:hover .msg-actions': { opacity: 1 } }}>
                {!mine && groupe && <Box sx={{ width: 30, flexShrink: 0 }}>{!suite && <UserAvatar id={m.user_id} nom={m.auteur} size={30} />}</Box>}
                {mine && peutSupprimer && (
                  <IconButton className="msg-actions" size="small" sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity .15s' }} aria-label="Options du message"
                    onClick={(e) => setMenu({ anchor: e.currentTarget, message: m })}><DeleteOutline sx={{ fontSize: 17, color: tokens.steel }} /></IconButton>
                )}
                <Box sx={{ maxWidth: { xs: '82%', sm: '68%' }, minWidth: 0 }}>
                  {!mine && groupe && !suite && <Typography sx={{ fontSize: 12, fontWeight: 600, color: couleur(m.user_id), ml: 1, mb: 0.25 }}>{m.auteur || 'Ancien utilisateur'}</Typography>}
                  <Box sx={{
                    px: 1.5, py: 0.9, borderRadius: 2.5, boxShadow: '0 1px 1px rgba(18,40,76,.08)',
                    borderTopRightRadius: mine && !suite ? 6 : undefined, borderTopLeftRadius: !mine && !suite ? 6 : undefined,
                    bgcolor: m.supprime ? 'transparent' : (mine ? tokens.navy : '#fff'), color: mine && !m.supprime ? '#fff' : tokens.ink,
                    border: m.supprime ? `1px dashed ${tokens.line}` : 'none',
                  }}>
                    {m.supprime ? (
                      <Typography sx={{ fontSize: 13.5, fontStyle: 'italic', color: tokens.steel }}>Message supprimé</Typography>
                    ) : (
                      <>
                        {m.fichier && (
                          <Box sx={{ mb: m.contenu ? 0.75 : 0, mt: 0.25 }}>
                            {estImage(m.fichier.type) ? <Image fichier={m.fichier} onOpen={(src) => setZoom({ src, fichier: m.fichier })} /> : <Fichier fichier={m.fichier} mine={mine} />}
                          </Box>
                        )}
                        {m.contenu && <Typography sx={{ fontSize: 14.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}><Texte>{m.contenu}</Texte></Typography>}
                      </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Typography sx={{ fontSize: 11, opacity: 0.65 }}>{heure(m.created_at)}</Typography>
                      {mine && !m.supprime && <DoneAll sx={{ fontSize: 15, color: luParTous >= m.id ? '#7FC8FF' : 'rgba(255,255,255,.55)' }} titleAccess={luParTous >= m.id ? (groupe ? 'Vu par tous' : 'Vu') : 'Envoyé'} />}
                    </Box>
                  </Box>
                  {mine && monDernier?.id === m.id && luParTous >= m.id && (
                    <Typography sx={{ fontSize: 11.5, color: tokens.steel, textAlign: 'right', mt: 0.25 }}>{groupe ? 'Vu par tous' : 'Vu'}</Typography>
                  )}
                </Box>
                {!mine && peutSupprimer && (
                  <IconButton className="msg-actions" size="small" sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity .15s' }} aria-label="Options du message"
                    onClick={(e) => setMenu({ anchor: e.currentTarget, message: m })}><DeleteOutline sx={{ fontSize: 17, color: tokens.steel }} /></IconButton>
                )}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>

      {/* Saisie */}
      <Box sx={{ borderTop: `1px solid ${tokens.line}`, bgcolor: '#fff', px: { xs: 1, sm: 2 }, py: 1.25 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        {fichier && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, borderRadius: 1.5, bgcolor: tokens.concrete }}>
            <IconeFichier type={fichier.type} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }}>{fichier.name}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{taille(fichier.size)} · ajoutez une légende si besoin</Typography>
            </Box>
            <IconButton size="small" onClick={() => setFichier(null)} aria-label="Retirer la pièce jointe"><CloseOutlined fontSize="small" /></IconButton>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <input ref={fileInput} type="file" hidden onChange={(e) => { choisirFichier(e.target.files?.[0]); e.target.value = ''; }} data-testid="chat-file" />
          <Tooltip title="Joindre une photo ou un fichier (10 Mo max)">
            <IconButton onClick={() => fileInput.current?.click()} aria-label="Joindre un fichier" sx={{ mb: 0.25 }}><AttachFileOutlined /></IconButton>
          </Tooltip>
          <TextField inputRef={input} fullWidth multiline maxRows={5} size="small" placeholder={fichier ? 'Légende (facultatif)' : 'Écrire un message…'}
            value={texte} onChange={(e) => setTexte(e.target.value)} inputProps={{ 'aria-label': 'Message', maxLength: 5000 }}
            onPaste={(e) => { const f = e.clipboardData?.files?.[0]; if (f) { e.preventDefault(); choisirFichier(f); } }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); envoyer(); } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#F7F8FA' } }} />
          <IconButton color="primary" onClick={envoyer} disabled={envoi || (!texte.trim() && !fichier)} aria-label="Envoyer"
            sx={{ bgcolor: tokens.navy, color: '#fff', mb: 0.25, '&:hover': { bgcolor: tokens.navySoft }, '&.Mui-disabled': { bgcolor: tokens.lineSoft, color: tokens.steel } }}>
            {envoi ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <SendRounded fontSize="small" />}
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5, ml: 6, display: { xs: 'none', sm: 'block' } }}>
          Entrée pour envoyer · Maj+Entrée pour aller à la ligne · glissez ou collez une photo
        </Typography>
      </Box>

      {drag && (
        <Box sx={{ position: 'absolute', inset: 8, border: `2px dashed ${tokens.navy}`, borderRadius: 2, bgcolor: 'rgba(255,255,255,.85)', display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
          <Typography sx={{ fontWeight: 600 }}>Déposez le fichier pour l'envoyer</Typography>
        </Box>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => supprimer(menu.message)} sx={{ color: 'error.main' }}><DeleteOutline fontSize="small" sx={{ mr: 1 }} />Supprimer le message</MenuItem>
      </Menu>
      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="lg">
        {zoom && (
          <>
            <Box component="img" src={zoom.src} alt={zoom.fichier.nom} sx={{ display: 'block', maxWidth: '100%', maxHeight: '80vh' }} />
            <DialogActions>
              <Typography sx={{ flex: 1, fontSize: 13, color: 'text.secondary', pl: 1 }} noWrap>{zoom.fichier.nom}</Typography>
              <Button startIcon={<DownloadOutlined />} onClick={() => telecharger(zoom.fichier)}>Télécharger</Button>
              <Button onClick={() => setZoom(null)}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <InfosGroupe open={infos} onClose={() => setInfos(false)} conv={conv} users={users} moiId={moiId}
        onChange={async () => { await chargerConv(); const r = await chatAPI.messages(id, { apres: dernierId }); setMessages((p) => [...p, ...r.data.messages.filter((m) => !p.some((x) => x.id === m.id))]); onActivity(); }}
        onQuitte={() => { setInfos(false); onQuitte(); }} />
    </Box>
  );
}

// ---------------------------------------------------------------------
// Utilisateurs connectés (administrateur)
// ---------------------------------------------------------------------
function Connectes({ onPrive }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => chatAPI.enLigne().then((r) => setData(r.data)).catch(() => {});
    load(); const t = setInterval(load, 20000); return () => clearInterval(t);
  }, []);
  if (!data) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  const ligne = (u) => (
    <ListItemButton key={u.id} onClick={() => !u.moi && onPrive(u.id)} disabled={u.moi} sx={{ '&.Mui-disabled': { opacity: 1 } }}>
      <ListItemAvatar><UserAvatar id={u.id} nom={u.nom} size={38} enLigne={u.en_ligne} /></ListItemAvatar>
      <ListItemText primary={`${u.nom}${u.moi ? ' (vous)' : ''}`} primaryTypographyProps={{ fontSize: 14, fontWeight: 500, noWrap: true }}
        secondary={[u.fonction, u.en_ligne ? 'en ligne' : depuis(u.last_seen)].filter(Boolean).join(' · ')} secondaryTypographyProps={{ fontSize: 12.5, noWrap: true }} />
    </ListItemButton>
  );
  return (
    <Box sx={{ overflowY: 'auto', flex: 1 }}>
      <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: 12, fontWeight: 700, color: '#1B7A46', display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CircleRounded sx={{ fontSize: 10 }} /> CONNECTÉS MAINTENANT ({data.connectes.length})
      </Typography>
      <List dense disablePadding>{data.connectes.map(ligne)}</List>
      {data.recents.length > 0 && (
        <>
          <Typography sx={{ px: 2, pt: 2, pb: 0.5, fontSize: 12, fontWeight: 700, color: tokens.steel }}>DERNIÈRES ACTIVITÉS</Typography>
          <List dense disablePadding>{data.recents.map(ligne)}</List>
        </>
      )}
      <Typography sx={{ px: 2, py: 1.5, fontSize: 12, color: 'text.secondary' }}>Connecté : actif dans l'ERP depuis moins de 2 minutes. Visible par les administrateurs seulement.</Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------
const Messages = () => {
  const { id } = useParams();
  const convId = id ? Number(id) : null;
  const navigate = useNavigate();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const { user, isAdmin } = useAuth();
  const [convs, setConvs] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [nouvelle, setNouvelle] = useState(false);
  const [onglet, setOnglet] = useState('discussions');
  const [error, setError] = useState('');

  useEffect(() => { document.title = 'Messages — DOCC'; }, []);
  const charger = useCallback(() => chatAPI.conversations().then((r) => setConvs(r.data)).catch((e) => setError(apiError(e))), []);
  useEffect(() => {
    charger();
    chatAPI.utilisateurs().then((r) => setUsers(r.data)).catch(() => {});
    const t = setInterval(charger, 8000);
    return () => clearInterval(t);
  }, [charger]);

  const ouvrirPrive = async (userId) => {
    try { const r = await chatAPI.prive(userId); setNouvelle(false); setOnglet('discussions'); await charger(); navigate(`/messages/${r.data.id}`); } catch (e) { setError(apiError(e)); }
  };
  const creerGroupe = async (data) => {
    const r = await chatAPI.groupe(data);
    setNouvelle(false); await charger(); navigate(`/messages/${r.data.id}`);
  };

  const liste = useMemo(() => (convs || []).filter((c) => !q || `${c.titre} ${(c.membres || []).map((m) => m.nom).join(' ')}`.toLowerCase().includes(q.toLowerCase())), [convs, q]);

  const apercu = (c) => {
    const d = c.dernier;
    if (!d) return c.type === 'GROUPE' ? 'Groupe créé' : 'Nouvelle discussion';
    if (d.supprime) return 'Message supprimé';
    if (d.systeme) return d.contenu;
    const qui = d.user_id === user?.id ? 'Vous : ' : (c.type === 'GROUPE' && d.auteur ? `${d.auteur.split(' ')[0]} : ` : '');
    return `${qui}${d.fichier ? `📎 ${d.contenu || d.fichier}` : d.contenu}`;
  };

  const panneauListe = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, bgcolor: '#fff' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ flex: 1, fontWeight: 700 }}>Messages</Typography>
        <Button variant="contained" size="small" startIcon={<AddCommentOutlined />} onClick={() => setNouvelle(true)}>Nouveau</Button>
      </Box>
      {isAdmin && (
        <Tabs value={onglet} onChange={(e, v) => setOnglet(v)} variant="fullWidth" sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 13.5 } }}>
          <Tab value="discussions" label="Discussions" />
          <Tab value="connectes" label="Connectés" />
        </Tabs>
      )}
      {onglet === 'connectes' && isAdmin ? <Connectes onPrive={ouvrirPrive} /> : (
        <>
          <Box sx={{ px: 2, py: 1 }}>
            <TextField fullWidth size="small" placeholder="Rechercher" value={q} onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
          </Box>
          {error && <Alert severity="error" sx={{ mx: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <List sx={{ overflowY: 'auto', flex: 1, py: 0 }} aria-label="Discussions">
            {convs === null && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
            {convs && !liste.length && (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography sx={{ fontSize: 14 }}>{q ? 'Aucune discussion trouvée' : 'Aucune discussion pour le moment'}</Typography>
                {!q && <Button sx={{ mt: 1 }} onClick={() => setNouvelle(true)}>Démarrer une discussion</Button>}
              </Box>
            )}
            {liste.map((c) => (
              <ListItemButton key={c.id} selected={c.id === convId} onClick={() => navigate(`/messages/${c.id}`)}
                sx={{ py: 1.1, gap: 1.5, borderLeft: '3px solid', borderColor: c.id === convId ? tokens.amber : 'transparent', '&.Mui-selected': { bgcolor: 'rgba(18,40,76,.06)' } }}>
                <UserAvatar id={c.interlocuteur?.id} nom={c.titre} groupe={c.type === 'GROUPE'} size={44} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography noWrap sx={{ flex: 1, fontSize: 14.5, fontWeight: c.non_lus ? 700 : 600 }}>{c.titre}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: c.non_lus ? '#1B7A46' : 'text.secondary', fontWeight: c.non_lus ? 700 : 400, flexShrink: 0 }}>
                      {quandCourt(c.dernier?.created_at || c.dernier_message_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography noWrap sx={{ flex: 1, fontSize: 13, color: c.non_lus ? tokens.ink : 'text.secondary', fontWeight: c.non_lus ? 500 : 400 }}>{apercu(c)}</Typography>
                    {c.non_lus > 0 && (
                      <Box sx={{ minWidth: 20, height: 20, px: 0.75, borderRadius: 10, bgcolor: '#1B7A46', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'grid', placeItems: 'center' }}
                        aria-label={`${c.non_lus} non lu(s)`}>{c.non_lus > 99 ? '99+' : c.non_lus}</Box>
                    )}
                  </Box>
                </Box>
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  const vide = (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', bgcolor: '#F3F5F8', textAlign: 'center', p: 3 }}>
      <Box>
        <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2, boxShadow: '0 1px 2px rgba(18,40,76,.1)' }}>
          <ChatBubbleOutline sx={{ fontSize: 32, color: tokens.navy }} />
        </Box>
        <Typography sx={{ fontWeight: 600, fontSize: 17 }}>Messagerie DOCC</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 14, mt: 0.5, maxWidth: 340 }}>
          Choisissez une discussion, ou démarrez-en une avec un collègue ou un groupe (équipe, chantier…).
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} startIcon={<AddCommentOutlined />} onClick={() => setNouvelle(true)}>Nouvelle discussion</Button>
      </Box>
    </Box>
  );

  const fil = convId ? (
    <Fil key={convId} id={convId} moiId={user?.id} isAdmin={isAdmin} users={users} onActivity={charger}
      onBack={desktop ? null : () => navigate('/messages')} onQuitte={() => { charger(); navigate('/messages'); }} />
  ) : vide;

  return (
    <Paper variant="outlined" sx={{
      display: 'flex', overflow: 'hidden', borderRadius: 2,
      height: { xs: 'calc(100dvh - 56px - 32px)', sm: 'calc(100dvh - 56px - 48px)', md: 'calc(100vh - 48px)' },
      mx: { xs: -0.5, sm: 0 },
    }}>
      {desktop ? (
        <>
          <Box sx={{ width: 350, flexShrink: 0, borderRight: `1px solid ${tokens.line}` }}>{panneauListe}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>{fil}</Box>
        </>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>{convId ? fil : panneauListe}</Box>
      )}
      <NouvelleDiscussion open={nouvelle} onClose={() => setNouvelle(false)} users={users} onPrive={ouvrirPrive} onGroupe={creerGroupe} />
    </Paper>
  );
};

export default Messages;
